import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useDrop } from "react-dnd"
import { FiCheck, FiPlay, FiPause, FiAlertTriangle } from "react-icons/fi"
import { FaMapMarkerAlt } from "react-icons/fa"
import { GiFactory } from "react-icons/gi"
import { FiPlusCircle } from "react-icons/fi"
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import "./index.scss"

import { Popup } from "components"
import { ActionConfig } from "components/Config"
import Button from "react-bootstrap/Button"
import isOnMobile from "utils/scripts/isOnMobile"

import { DateTime } from "luxon"
import { chooseOptimalColor, optimalColor } from "utils/scripts/schedule"
import dragTypes from "utils/scripts/dragTypes"
import useCustomDrag from "utils/scripts/useCustomDrag"

import { projectActions, projects as projectState, standardActions, visual } from "state_management"
import { FilterActionsConfig, UnplannedToPlannedConfig } from "components/Config"
import { Text } from "components/Form"
import StandardActions from "./StandardActions"
import ProjectConfigurationPage from "components/Config/ProjectConfigurationPage"

// The To Do list in team-plan
export default function ProjectTasksMenu() {
  const projects = useSelector((state) => state.projects)
  const unplannedProjectActions = useSelector((state) => state.projectActions.unplannedProjectActions)
  const allStandardActionTemplates = useSelector((state) => state.standardActions.standardActions)
  const allStandardActions = allStandardActionTemplates.reduce((acc, template) => {
    return acc.concat(template.actions);
  }, []);
  const [activeActionId, setActiveActionId] = useState(null)
  const activeAction = useSelector(
    (state) => (activeActionId && state.projectActions.unplannedProjectActions[activeActionId]) || null
  )

  const [createSingleAction, setCreateSingleAction] = useState(null)
  const [createStandardAction, setCreateStandardAction] = useState(null)
  const [activeStandardTaskId, setActiveStandardTaskId] = useState(null)
  const activeStandardAction = useSelector((state) => allStandardActions.find((action) => action.id === activeStandardTaskId) || null)
  const showStandardActions = useSelector((state) => state.visual.teamPlan.showStandardActions)
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [showFilterConfig, setShowFilterConfig] = useState(false)
  const [unplannedToPlannedConfig, setUnplannedToPlannedConfig] = useState({ res: null, id: null })
  const filterSettings = useSelector((state) => state.visual.filterSettings)
  const [textFilter, setTextFilter] = useState("")
  const dispatch = useDispatch()
  const [editProject, setEditProject] = useState(false);

  const [{ isOver }, drop] = useDrop( () => ({
    accept: dragTypes.ACTION,
    collect: (mon) => ({
      isOver: !!mon.isOver(),
    }),
    drop: ({ item }) => {
      dispatch(projectActions.removePlanned(item.id))
    },
  }), [dragTypes.ACTION])

  const renderProjects = () => {
    // Get unique project IDs from unplanned actions
    const projectIds = new Set();
    Object.values(unplannedProjectActions).forEach(action => {
      projectIds.add(action.projectId);
    });

    // Filter projects based on text search if needed
    let filteredProjectIds = [...projectIds];
    if (textFilter) {
      const searchText = textFilter.toLowerCase();
      filteredProjectIds = filteredProjectIds.filter(id => {
        const project = projects[id];
        return project && (project.name.toLowerCase().includes(searchText) ||
          project.description?.toLowerCase().includes(searchText));
      });
    }

    // Sort projects by deadline and priority
    return filteredProjectIds
      .sort((idA, idB) => {
        const projectA = projects[idA];
        const projectB = projects[idB];

        // Sort by deadline first
        if (projectA.deadline && projectB.deadline) {
          if (projectA.deadline < projectB.deadline) return -1;
          if (projectA.deadline > projectB.deadline) return 1;
        } else if (projectA.deadline) return -1;
          else if (projectB.deadline) return 1;

        // Then by priority
        if (projectA.priority < projectB.priority) return -1;
        if (projectA.priority > projectB.priority) return 1;

        // Finally by name
        return projectA.name.localeCompare(projectB.name);
      })
      .map((projectId) => {
        const project = projects[projectId];
        const projectActions = Object.values(unplannedProjectActions).filter(
          action => action.projectId === projectId
        );

        return (
          <ProjectItem
            key={projectId}
            project={project}
            actionCount={projectActions.length}
            onClick={() => {
              setEditProject(true);
              setActiveProjectId(projectId)
            }}
          />
        );
      });
  }

  return (
    <>
      {editProject && (
        <Popup
          className="Edit-Create-Project"
          onCancel={() => {
            setEditProject(false)
            setActiveProjectId(null)
          }}
        >
          <ProjectConfigurationPage
            isCreateOrEditProject={true}
            id={activeProjectId} // Only add id when we're editing an existing project
            setEditProject={() => {
              setEditProject(false)
              setActiveProjectId(null) // Clear the active project when done
            }}
          />
        </Popup>
      )}
      {activeActionId && unplannedProjectActions[activeActionId] && (
        <Popup nonScrollable onCancel={() => setActiveActionId(null)}>
          <ActionConfig
            title={"Edit Action"}
            action={unplannedProjectActions[activeActionId]}
            cancelHandler={() => setActiveActionId(null)}
            deleteHandler={
              projects[activeAction.projectId] && projects[activeAction.projectId].singleActionProject
                ? () => {
                    dispatch(projectState.remove(activeAction.projectId))
                    setActiveActionId(null)
                  }
                : null
            }
            isSingleAction={projects[activeAction.projectId] && projects[activeAction.projectId].singleActionProject}
            submitHandler={(res) => {
              if (res.stage === "PLANNED") {
                setUnplannedToPlannedConfig({ res, id: activeActionId })
                return
              }
              dispatch(projectActions.editUnplanned(activeActionId, res)).then(() => setActiveActionId(null))
            }}
            projectId={!isOnMobile ? activeAction.projectId : null}
          />
        </Popup>
      )}
      {createSingleAction && (
        <Popup nonScrollable onCancel={() => setCreateSingleAction(null)}>
          <ActionConfig
            title={"Create Action"}
            isSingleAction
            action={{}}
            cancelHandler={() => setCreateSingleAction(null)}
            submitHandler={(res) => {
              dispatch(projectActions.createSingleAction({ ...res, tempId: "123456789" })).then(() =>
                setCreateSingleAction(null)
              )
            }}
          />
        </Popup>
      )}
      {unplannedToPlannedConfig.res && (
        <Popup
          nonScrollable
          onCancel={() => {
            setActiveActionId(unplannedToPlannedConfig.id)
            setUnplannedToPlannedConfig({ res: null, id: null })
          }}
        >
          <UnplannedToPlannedConfig
            cancelHandler={() => {
              setActiveActionId(unplannedToPlannedConfig.id)
              setUnplannedToPlannedConfig({ res: null, id: null })
            }}
            cancelAll={() => {
              setActiveActionId(null)
              setUnplannedToPlannedConfig({ res: null, id: null })
            }}
            task={unplannedProjectActions[unplannedToPlannedConfig.id]}
            res={unplannedToPlannedConfig.res}
          />
        </Popup>
      )}

      {activeStandardTaskId && activeStandardAction && (
        <Popup
          nonScrollable
          onCancel={() => setActiveStandardTaskId(null)}
        >
          <ActionConfig
            title={"Edit Standard Action"}
            action={activeStandardAction}
            cancelHandler={() => setActiveStandardTaskId(null)}
            submitHandler={(res) => {
              dispatch(standardActions.editStandardAction(activeStandardTaskId, res)).then(() => {
                setActiveStandardTaskId(null)
              })
            }}
            disabled={true}
            deleteHandler={() => {
              dispatch(standardActions.deleteStandardAction(activeStandardTaskId)).then(() => {
                setActiveStandardTaskId(null)
              })
            }}
            projectId={!isOnMobile ? activeStandardAction.projectId : null}
            deleteTitle="Delete"
          />
        </Popup>
      )}

      {createStandardAction && (
        <Popup nonScrollable onCancel={() => setCreateStandardAction(null)}>
          <ActionConfig
            title={"Create Standard Action"}
            isSingleAction
            action={{}}
            cancelHandler={() => setCreateStandardAction(null)}
            submitHandler={(res) => {
              dispatch(standardActions.createStandardActions({...res, tempId: "123456789" })).then(() =>
                setCreateStandardAction(null)
              )
            }}
            disabled={true}
          />
        </Popup>
      )}

      <div
        className="ProjectTasksMenu-main-container"
        ref={drop}
        style={{
          filter: isOver ? "brightness(50%)" : "none",
        }}
      >
        <div className="standard-action-container">
          <div className="standard-action-dropdown" onClick={() => dispatch(visual.setShowStandardActions(!showStandardActions))}>
            <h5>Standard Actions</h5>
            {showStandardActions ? <IoIosArrowUp/> : <IoIosArrowDown/>}
          </div>
          <Button
            title="Create Standard Action"
            onClick={() => setCreateStandardAction(true)}
          >
            <FiPlusCircle/>
          </Button>
        </div>

        {showStandardActions && (
          <div>
            <StandardActions setActiveStandardTaskId={setActiveStandardTaskId}/>
          </div>
        )}

        <hr/>
        <div className="ProjectTasksMenu-tasks-container">
          {showFilterConfig && <FilterActionsConfig cancelHandler={() => setShowFilterConfig(false)}></FilterActionsConfig>}
          <div className="FilterButtons-Container">
            <h5 className="Pheading ActionList-Caption">Unplanned Projects</h5>
            <div className="todoList-Buttons">
              <Button
                className="Filter-button"
                title="Create new project"
                variant="primary"
                onClick={() => setEditProject(true)}
              >
                <p>Create New Project</p>
              </Button>

              {/* Create singe action button */}
              {/* <Button
                className="Filter-button"
                title="Create Single Action"
                variant="primary"
                onClick={() => setCreateSingleAction(true)}
              >
                <FiPlusCircle color="white" />
              </Button> */}
            </div>
            <div className="Todolist-Textfilter-Filterlist">
              <Text value={textFilter} onChange={setTextFilter} placeholder="Filter by Text"></Text>
              <Button className="Filter-button" onClick={() => setShowFilterConfig(true)}>
                <p>Filter</p>
              </Button>
            </div>
              <ul className="list-unstyled">
                {filterSettings ? filterSettings.isDeadlineFilter && <li>By Deadline</li> : ""}
                {filterSettings ? filterSettings.isCompetenceGroupFilter && <li>By Group</li> : ""}
                {textFilter ? <li>By Text</li> : ""}
                {filterSettings ? filterSettings.scrumViewOrder && <li>Scrum View Ordering</li> :""}
              </ul>
          </div>
          {projects && renderProjects()}
        </div>
      </div>
    </>
  )
}

function ProjectItem({ project, actionCount, onClick }) {
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone);
  const unplannedProjectActions = useSelector((state) => state.projectActions.unplannedProjectActions);

  const actionsWithWarnings = Object.values(unplannedProjectActions)
    .filter(action => action.projectId === project.id && action.iapWarning && action.iapWarning.length > 0);

  const warningMessage = actionsWithWarnings.length > 0 ?
    actionsWithWarnings.map(action => `${action.name}: ${action.iapWarning}`).join('\n') : '';

  const { isDragging, dragRef } = useCustomDrag(dragTypes.PROJECT, project);

  return (
    <div
      ref={dragRef}
      className="ProjectTasksMenu-analysis-container"
      onClick={onClick}
      style={{
        opacity: isDragging ? "0.4" : undefined,
        backgroundColor: project.color,
        color: chooseOptimalColor(project.color),
        cursor: "pointer",
        boxShadow: project.deadline &&
          DateTime.fromISO(project.deadline).setZone(teamTimeZone).startOf("day") <
            DateTime.local().setZone(teamTimeZone).startOf("day")
          ? "0px 0px 0px 6px red"
          : "",
      }}
    >
      <div className="ProjectTasksMenu-text">
        <div className="flex-container">
          <h5>{project.name}</h5>
        </div>
        <p>
          Deadline: {project.deadline
            ? DateTime.fromISO(project.deadline).setZone(teamTimeZone).toFormat("ff")
            : "None"}
        </p>
        <p>Actions: {actionCount}</p>
      </div>

      {actionsWithWarnings.length > 0 && (
        <div title={warningMessage} className="hoverable-container">
          <div className="project-yellow-warning-container">
            <FiAlertTriangle />
          </div>
        </div>
      )}
    </div>
  );
}
export function UnplannedProjectActionItem({ action, onClick }) {
  const projects = useSelector((state) => state.projects)
  const groups = useSelector((state) => state.competenceGroups)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const dispatch = useDispatch()
  const allStandardActions = useSelector((state) => state.standardActions.standardActions)


  const project = action.isStandardAction
  ? allStandardActions.find((standardAction) =>
  standardAction.actions.some((actionItem) => actionItem.id === action.id)
  )
  : projects[action.projectId];

  const { isDragging, dragRef } = useCustomDrag(action.isStandardAction ? dragTypes.STANDARD_ACTION : dragTypes.ACTION, action)

  // This is conditionally rendered, so when unplanning a standard action, it doesn't throw an error because the actions projectId is also removed from the database
  if (project) return (
    <div
      ref={dragRef}
      className={`ProjectTasksMenu-analysis-container` + (action.isStandardAction ? "-standard-action" : "")}
      onClick={onClick}
      style={{
        opacity: isDragging ? "0.4" : undefined,
        backgroundColor: project.color,
        cursor: "pointer",
        color: chooseOptimalColor(project.color),
        boxShadow:
          action.isStandardAction ?
          "0px 0px 0px 0px #000000"
          :
          action.deadline || project.deadline
            ? (action.deadline && DateTime.fromISO(action.deadline).setZone(teamTimeZone).startOf("day") < DateTime.local().setZone(teamTimeZone).startOf("day")) ||
              (!action.deadline &&
                project.deadline &&
                DateTime.fromISO(project.deadline).setZone(teamTimeZone).startOf("day") < DateTime.local().setZone(teamTimeZone).startOf("day"))
              ? "0px 0px 0px 6px red"
              : ""
            : "",
      }}
    >
      {!action.isStandardAction &&
        <div className="UnplannedProjectAction-Onsite">
          {!action.onSite && (
            <FaMapMarkerAlt
              title={"Can be performed anywhere"}
              onClick={(e) => {
                dispatch(projectActions.setLocationUnplanned(action.id, true))
                e.stopPropagation()
              }}
            />
          )}
          {action.onSite && (
            <GiFactory
              title={"Can only be performed on site"}
              color={optimalColor(project.color,"blue","orange")}
              onClick={(e) => {
                dispatch(projectActions.setLocationUnplanned(action.id, true))
                e.stopPropagation()
              }}
            />
          )}
        </div>
      }
      {action.state === "STARTED" ? (
        <FiPlay />
      ) : action.state === "PAUSED" ? (
        <FiPause />
      ) : action.state === "STOPPED" ? (
        <FiCheck />
      ) : null}
      <div className="ProjectTasksMenu-text">
        <div className="flex-container">
          {project.singleActionProject ? null : <h5 style={{margin:"0"}}>{project.name + ":"}&nbsp;</h5>}
          <h5>{action.name}</h5>
        </div>
        {!action.isStandardAction && (
          <>
            <p>
              Deadline:{" "}
              {action.deadline
                ? DateTime.fromISO(action.deadline).setZone(teamTimeZone).toFormat("ff")
                : project.deadline
                ? DateTime.fromISO(project.deadline).setZone(teamTimeZone).toFormat("ff")
                : ""}
            </p>
            <p>
              {"Competence Groups: " +
                action.allowedGroupIds
                  .filter((groupId) => groups[groupId])
                  .map((groupId) => groups[groupId].name)
                  .sort()
                  .join(", ")}
            </p>
          </>
        )}
      </div>
      {action.iapWarning && action.iapWarning.length > 0 && (
        <div title={action.iapWarning} className="hoverable-container">
          <div className="project-yellow-warning-container">
            <FiAlertTriangle />
          </div>
        </div>
      )}
    </div>
  )
}
